import React, { useState, useEffect } from 'react';
import ProjektDetail from './projektDetail';
import { apiFetch } from './api';

const MeineProjekte = ({ user }) => {
  const [projekte, setProjekte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offenesProjekt, setOffenesProjekt] = useState(null);
  const [filter, setFilter] = useState('alle');
  const [scope, setScope] = useState('meine');

  const lade = async () => {
    setLoading(true);
    let res;
    if (scope === 'meine') {
      res = await apiFetch(`/meine/projekte?user_id=${user.id}`);
    } else {
      res = await apiFetch('/alle/projekte');
    }
    const data = await res.json();
    setProjekte(data);
    setLoading(false);
  };

  useEffect(() => { lade(); }, [scope]);

  const statusFarbe = (status) => {
    const f = { offen: '#9b9b9b', 'in arbeit': '#1565c0', erledigt: '#2e7d32' };
    return f[status] || '#9b9b9b';
  };

  const badge = (prio) => {
    const farben = {
      hoch: { bg: '#FCEBEB', text: '#A32D2D' },
      mittel: { bg: '#FAEEDA', text: '#854F0B' },
      niedrig: { bg: '#EAF3DE', text: '#3B6D11' }
    };
    const f = farben[prio] || farben.mittel;
    return (
      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', backgroundColor: f.bg, color: f.text, textTransform: 'capitalize' }}>
        {prio}
      </span>
    );
  };

  const formatDeadline = (dl) => {
    if (!dl) return '—';
    const d = new Date(dl);
    return `${d.getDate()}.${d.getMonth() + 1}.`;
  };

  const typLabel = (typ) => {
    if (typ === 'produktion') return 'Produktion';
    if (typ === 'format') return 'Format';
    if (typ === 'backoffice') return 'Backoffice';
    return typ;
  };

  const gefiltert = projekte.filter(p => {
    if (filter === 'archiv') return p.status === 'erledigt';
    if (filter === 'alle') return p.status !== 'erledigt';
    if (filter === 'offen') return p.status === 'offen';
    if (filter === 'arbeit') return p.status === 'in arbeit';
    return true;
  });

  const gruppen = {};
  gefiltert.forEach(p => {
    const key = `${p.parent_typ}-${p.parent_id}`;
    if (!gruppen[key]) gruppen[key] = { typ: p.parent_typ, name: p.mutter_name, farbe: p.mutter_farbe, projekte: [] };
    gruppen[key].projekte.push(p);
  });

  const projektZeile = (p) => (
    <div key={p.id} onClick={() => setOffenesProjekt(p.id)} style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', paddingLeft: '10px',
      borderBottom: '1px solid #f4f4f4', cursor: 'pointer',
      borderLeft: p.mutter_farbe ? `3px solid ${p.mutter_farbe}` : '3px solid transparent',
    }}>
      <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: statusFarbe(p.status), flexShrink: 0 }} />
      <span style={{ fontSize: '14px', color: '#1a1a1a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
      {badge(p.prioritaet)}
      <span style={{ fontSize: '12px', color: '#9b9b9b', minWidth: '50px', textAlign: 'right', flexShrink: 0 }}>{formatDeadline(p.deadline)}</span>
    </div>
  );

  const filterBtn = (key, label) => (
    <span onClick={() => setFilter(key)} style={{
      fontSize: '12px', padding: '5px 12px', borderRadius: '20px', cursor: 'pointer',
      border: '1px solid ' + (filter === key ? '#1a1a1a' : '#e8e8e8'),
      backgroundColor: filter === key ? '#1a1a1a' : 'transparent',
      color: filter === key ? '#fff' : '#6b6b6b', fontWeight: filter === key ? '500' : '400'
    }}>{label}</span>
  );

  if (offenesProjekt) {
    return <ProjektDetail projektId={offenesProjekt} onZurueck={() => { setOffenesProjekt(null); lade(); }} />;
  }

  if (loading) return <div style={{ padding: '40px', color: '#9b9b9b', fontSize: '14px' }}>Lädt...</div>;

  const aktiveAnzahl = projekte.filter(p => p.status !== 'erledigt').length;
  const gruppenListe = Object.values(gruppen);

  return (
    <div style={{ padding: '40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px' }}>
        <div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
            {scope === 'meine' ? 'Meine Projekte' : 'Alle Projekte'}
          </h2>
          <p style={{ fontSize: '14px', color: '#9b9b9b', margin: 0 }}>
            {aktiveAnzahl} {aktiveAnzahl === 1 ? 'aktives Projekt' : 'aktive Projekte'}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Meine / Alle Switch */}
          <div style={{ display: 'flex', border: '1px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
            {[{ key: 'meine', label: 'Meine' }, { key: 'alle', label: 'Alle' }].map(s => (
              <button key={s.key} onClick={() => { setScope(s.key); setFilter('alle'); }} style={{
                padding: '6px 14px', border: 'none', fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                backgroundColor: scope === s.key ? '#1a1a1a' : '#ffffff',
                color: scope === s.key ? '#ffffff' : '#6b6b6b',
              }}>{s.label}</button>
            ))}
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {filterBtn('alle', 'Alle')}
            {filterBtn('offen', 'Offen')}
            {filterBtn('arbeit', 'In Arbeit')}
            {filterBtn('archiv', 'Archiv')}
          </div>
        </div>
      </div>

      {gruppenListe.length === 0 ? (
        <p style={{ fontSize: '14px', color: '#c4c4c4' }}>
          {filter === 'archiv' ? 'Noch nichts im Archiv.' : 'Keine Projekte gefunden.'}
        </p>
      ) : (
        gruppenListe.map((g, i) => (
          <div key={i} style={{ marginBottom: '28px' }}>
            <p style={{
              fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
              color: g.farbe || '#9b9b9b',
              borderLeft: g.farbe ? `3px solid ${g.farbe}` : 'none',
              paddingLeft: g.farbe ? '8px' : '0'
            }}>
              {typLabel(g.typ)} · {g.name}
            </p>
            <div>{g.projekte.map(projektZeile)}</div>
          </div>
        ))
      )}
    </div>
  );
};

export default MeineProjekte;